import request from "react-rb/request"

/**
 * Manages JWT tokens for websocket authentication with automatic refresh and localStorage caching.
 * Provides token validation, storage management, and deduplication of concurrent fetch requests.
 */
class JwtTokenManager {
  constructor() {
    this.storageKey = "websocket_jwt_token"
    this.expirationKey = "websocket_jwt_token_expires_at"
    this.fetchPromise = null
  }

  /**
   * Gets a valid JWT token, either from cache or by fetching a new one.
   * Automatically clears invalid/expired tokens and fetches fresh ones as needed.
   * @returns {Promise<string>} A valid JWT token string
   * @throws {Error} When token fetch fails
   */
  async getValidToken() {
    const storedToken = this.getStoredToken()

    if (storedToken && this.isTokenValid(storedToken)) {
      return storedToken.token
    }

    this.clearStoredToken()

    return this.fetchNewToken()
  }

  /**
   * Retrieves the stored JWT token and expiration from localStorage.
   * @returns {Object|null} Token data object with token and expiresAt properties, or null if not found
   * @returns {string} returns.token - The JWT token string
   * @returns {Date} returns.expiresAt - Token expiration date
   */
  getStoredToken() {
    try {
      const token = localStorage.getItem(this.storageKey)
      const expiresAt = localStorage.getItem(this.expirationKey)

      if (!token || !expiresAt) {
        return null
      }

      return {
        token,
        expiresAt: new Date(expiresAt)
      }
    } catch (error) {
      console.warn("Failed to read stored JWT token:", error)
      return null
    }
  }

  /**
   * Validates if a token is still valid (not expired with 5-minute safety buffer).
   * @param {Object} tokenData - Token data object with expiresAt property
   * @param {Date} tokenData.expiresAt - Token expiration date
   * @returns {boolean} True if token is valid and not expired (with buffer), false otherwise
   */
  isTokenValid(tokenData) {
    if (!tokenData || !tokenData.expiresAt) {
      return false
    }

    const now = new Date()
    const bufferTime = 5 * 60 * 1000 // 5 minutes in milliseconds
    const expirationWithBuffer = new Date(
      tokenData.expiresAt.getTime() - bufferTime
    )

    return now < expirationWithBuffer
  }

  /**
   * Fetches a new JWT token from the backend API with request deduplication.
   * Prevents multiple simultaneous fetch requests by reusing pending promises.
   * @returns {Promise<string>} A fresh JWT token string
   * @throws {Error} When token fetch fails
   */
  async fetchNewToken() {
    // Prevent multiple simultaneous requests
    if (this.fetchPromise) {
      return this.fetchPromise
    }

    this.fetchPromise = this._performTokenFetch()

    try {
      const result = await this.fetchPromise
      return result
    } finally {
      this.fetchPromise = null
    }
  }

  /**
   * Internal method to perform the actual token fetch from the API.
   * Makes POST request to /internal/websocket_auth/token and stores the result.
   * @private
   * @returns {Promise<string>} The fetched JWT token string
   * @throws {Error} When API request fails or returns invalid data
   */
  async _performTokenFetch() {
    try {
      const response = await request("/internal/websocket_auth/token", {
        method: "POST"
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch JWT token: ${response.status} ${response.statusText}`
        )
      }

      const data = response.data

      if (!data.token || !data.expires_at) {
        throw new Error("Invalid token response from server")
      }

      // Store token and expiration
      this.storeToken(data.token, data.expires_at)

      return data.token
    } catch (error) {
      console.error("Failed to fetch websocket JWT token:", error)
      throw error
    }
  }

  /**
   * Stores JWT token and expiration date in localStorage.
   * @param {string} token - The JWT token string to store
   * @param {string} expiresAt - ISO date string of token expiration
   */
  storeToken(token, expiresAt) {
    try {
      localStorage.setItem(this.storageKey, token)
      localStorage.setItem(this.expirationKey, expiresAt)
    } catch (error) {
      console.warn("Failed to store JWT token:", error)
    }
  }

  /**
   * Removes stored JWT token and expiration from localStorage.
   * Used when tokens are invalid or expired.
   */
  clearStoredToken() {
    try {
      localStorage.removeItem(this.storageKey)
      localStorage.removeItem(this.expirationKey)
    } catch (error) {
      console.warn("Failed to clear stored JWT token:", error)
    }
  }

  /**
   * Forces a token refresh by clearing stored token and fetching a new one.
   * Useful for testing or when authentication state changes.
   * @returns {Promise<string>} A fresh JWT token string
   * @throws {Error} When token fetch fails
   */
  async refreshToken() {
    this.clearStoredToken()
    return this.fetchNewToken()
  }
}

/**
 * Singleton instance of JwtTokenManager for application-wide use.
 * @type {JwtTokenManager}
 */
const jwtTokenManager = new JwtTokenManager()

/**
 * Export the class for testing purposes.
 */
export { JwtTokenManager, jwtTokenManager }
